import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Table, Form, Select, InputNumber, Button, Space, message, Card, Divider, Tag, Popconfirm, Empty, Input } from 'antd';
import { PlusOutlined, DeleteOutlined, SwapOutlined, EditOutlined, CheckCircleFilled, CheckOutlined } from '@ant-design/icons';
import { urunRecetelerAPI, malzemelerAPI, malzemeBirimDonusumleriAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatSayi } from '../utils/format';
import BirimDonusumModal from './BirimDonusumModal';
import dayjs from 'dayjs';

function ReceteModal({ open, urun, onClose, onSaved }) {
  const { birimler: birimlerRaw, birimOptions: birimler, malzemeTipiOptions: malzemeTipleri } = useLookups();
  const [malzemeler, setMalzemeler] = useState([]);
  const [birimDonusumleri, setBirimDonusumleri] = useState([]);
  const [receteler, setReceteler] = useState([]); // tüm reçeteler (aktif + pasif)
  const [editId, setEditId] = useState(null); // null=yeni, sayı=mevcut güncelle
  const [form] = Form.useForm();
  const [donusumOpen, setDonusumOpen] = useState(false);
  const [donusumMalzeme, setDonusumMalzeme] = useState(null);
  const [malzemeArama, setMalzemeArama] = useState('');
  const [malzemeModalOpen, setMalzemeModalOpen] = useState(false);
  const [malzemeForm] = Form.useForm();

  const idByAd = useMemo(() => Object.fromEntries(birimlerRaw.map(b => [b.ad, b.id])), [birimlerRaw]);
  const birimGruplari = useMemo(() => [
    [idByAd['kg'], idByAd['gr']].filter(Boolean),
    [idByAd['lt'], idByAd['ml']].filter(Boolean),
  ], [idByAd]);

  const fetchMalzemeler = async () => {
    try {
      const res = await malzemelerAPI.getAll();
      setMalzemeler(res.data);
    } catch {}
  };
  const fetchBirimDonusumleri = async () => {
    try {
      const res = await malzemeBirimDonusumleriAPI.getAllSon();
      setBirimDonusumleri(res.data);
    } catch {}
  };

  const fetchReceteler = async () => {
    if (!urun?.id) { setReceteler([]); return; }
    try {
      const res = await urunRecetelerAPI.getByUrun(urun.id);
      setReceteler(res.data || []);
    } catch {
      setReceteler([]);
    }
  };

  useEffect(() => {
    if (open) {
      fetchMalzemeler();
      fetchBirimDonusumleri();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !urun?.id) return;
    fetchReceteler();
    setEditId(null);
    form.resetFields();
  }, [open, urun?.id]);

  // Düzenleme moduna geç (mevcut bir reçeteyi forma yükle).
  // giris_miktar/giris_birim_id varsa kullanıcının GİRDİĞİ değer birebir geri
  // yüklenir. Eski kayıtlarda bu alanlar boş olabilir; o zaman eski davranışa
  // (1 porsiyonluk miktarı porsiyonla çarpmaya) dönülür — yuvarlama yüzünden
  // yaklaşık olabilir.
  const handleEditAc = (recete) => {
    setEditId(recete.id);
    const porsiyon = recete.porsiyon || 1;
    form.setFieldsValue({
      recetePorsiyon: porsiyon,
      kalemler: (recete.kalemler || []).map(k => {
        const arsivVar = k.giris_miktar !== null && k.giris_miktar !== undefined;
        return {
          malzeme_id: k.malzeme_id,
          miktar: arsivVar
            ? Number(k.giris_miktar)
            : Number(k.gosterim_miktar || k.miktar) * porsiyon,
          birim_id: arsivVar
            ? (k.giris_birim_id || k.gosterim_birim_id || k.birim_id)
            : (k.gosterim_birim_id || k.birim_id),
        };
      }),
    });
  };
  const handleYeniAc = () => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue({ recetePorsiyon: 1 });
  };

  const handleAktiveEt = async (id) => {
    try {
      await urunRecetelerAPI.aktiveEt(id);
      message.success('Reçete aktif yapıldı');
      await fetchReceteler();
      onSaved?.();
    } catch (err) {
      message.error('Aktifleştirilemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleSil = async (id) => {
    try {
      await urunRecetelerAPI.remove(id);
      message.success('Reçete silindi');
      if (editId === id) { setEditId(null); form.resetFields(); }
      await fetchReceteler();
      onSaved?.();
    } catch (err) {
      message.error(err.response?.data?.hata || 'Silinemedi');
    }
  };

  const donusturKalem = (k, porsiyon) => {
    // Kullanıcının girdiği ham değer her durumda arşivlenir; düzenlemede
    // bölme/çarpma kaybı olmadan geri yüklenebilsin diye.
    const arsiv = { giris_miktar: k.miktar, giris_birim_id: k.birim_id };
    const m = malzemeler.find(mal => mal.id === k.malzeme_id);
    if (!m) return { ...arsiv, malzeme_id: k.malzeme_id, miktar: k.miktar / porsiyon, birim_id: k.birim_id };

    const secilenBirimId = k.birim_id;
    const malzemeBirimId = m.birim_id;
    const grup = birimGruplari.find(g => g.includes(malzemeBirimId));
    const grupBirimIds = grup ? grup : [malzemeBirimId];

    if (grupBirimIds.includes(secilenBirimId)) {
      return {
        ...arsiv,
        malzeme_id: k.malzeme_id,
        miktar: k.miktar / porsiyon,
        birim_id: secilenBirimId,
      };
    }

    const adById = Object.fromEntries(birimlerRaw.map(b => [b.id, b.ad]));
    const sabitCarpan = (fromId, toId) => {
      if (fromId === toId) return 1;
      const f = adById[fromId], t = adById[toId];
      if ((f === 'gr' && t === 'kg') || (f === 'ml' && t === 'lt')) return 0.001;
      if ((f === 'kg' && t === 'gr') || (f === 'lt' && t === 'ml')) return 1000;
      return null;
    };

    let donusum = birimDonusumleri.find(
      d => d.malzeme_id === k.malzeme_id && d.kaynak_birim_id === secilenBirimId
    );
    let onCarpan = 1;

    if (!donusum) {
      const secilenGrup = birimGruplari.find(g => g.includes(secilenBirimId));
      if (secilenGrup) {
        for (const grupBirimId of secilenGrup) {
          if (grupBirimId === secilenBirimId) continue;
          donusum = birimDonusumleri.find(
            d => d.malzeme_id === k.malzeme_id && d.kaynak_birim_id === grupBirimId
          );
          if (donusum) {
            onCarpan = sabitCarpan(secilenBirimId, grupBirimId);
            break;
          }
        }
      }
    }

    if (donusum && grupBirimIds.includes(donusum.hedef_birim_id)) {
      const hedefBirimId = donusum.hedef_birim_id;
      const donusturulmusMiktar = k.miktar * onCarpan * donusum.carpan;
      let asilMiktar = donusturulmusMiktar;
      let asilBirimId = hedefBirimId;
      const sonCarpan = sabitCarpan(hedefBirimId, malzemeBirimId);
      if (sonCarpan && hedefBirimId !== malzemeBirimId) {
        asilMiktar = donusturulmusMiktar * sonCarpan;
        asilBirimId = malzemeBirimId;
      }
      return {
        ...arsiv,
        malzeme_id: k.malzeme_id,
        miktar: asilMiktar / porsiyon,
        birim_id: asilBirimId,
        gosterim_miktar: k.miktar / porsiyon,
        gosterim_birim_id: secilenBirimId,
      };
    }

    return {
      ...arsiv,
      malzeme_id: k.malzeme_id,
      miktar: k.miktar / porsiyon,
      birim_id: secilenBirimId,
    };
  };

  const getSecilebilenBirimler = (malzemeId) => {
    const m = malzemeler.find(mal => mal.id === malzemeId);
    if (!m) return [];
    const grup = birimGruplari.find(g => g.includes(m.birim_id));
    const grupBirimIds = grup ? [...grup] : [m.birim_id];
    const donusumEkIds = [];
    birimDonusumleri
      .filter(d => d.malzeme_id === malzemeId)
      .forEach(d => {
        donusumEkIds.push(d.kaynak_birim_id);
        const kaynakGrup = birimGruplari.find(g => g.includes(d.kaynak_birim_id));
        if (kaynakGrup) kaynakGrup.forEach(id => donusumEkIds.push(id));
        const hedefGrup = birimGruplari.find(g => g.includes(d.hedef_birim_id));
        if (hedefGrup) hedefGrup.forEach(id => donusumEkIds.push(id));
      });
    const tumIds = [...new Set([...grupBirimIds, ...donusumEkIds])];
    return birimler.filter(b => tumIds.includes(b.value));
  };

  const handleMalzemeChange = (malzemeId, fieldName) => {
    const m = malzemeler.find(mal => mal.id === malzemeId);
    if (m) form.setFieldValue(fieldName, m.birim_id);
  };

  const malzemeOptions = malzemeler.map(m => ({ value: m.id, label: m.ad }));

  const handleHizliMalzemeEkle = () => {
    malzemeForm.setFieldsValue({ ad: malzemeArama });
    setMalzemeModalOpen(true);
  };

  const handleMalzemeKaydet = async (values) => {
    try {
      await malzemelerAPI.create(values);
      message.success('Malzeme eklendi');
      setMalzemeModalOpen(false);
      malzemeForm.resetFields();
      await fetchMalzemeler();
    } catch (err) {
      message.error('Malzeme eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const malzemeNotFound = (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <p style={{ color: '#999', marginBottom: 8 }}>
        "{malzemeArama}" bulunamadı
      </p>
      <Button type="link" onClick={handleHizliMalzemeEkle}>
        Malzeme olarak eklemek için tıklayın
      </Button>
    </div>
  );

  const malzemeSelectProps = {
    placeholder: 'Malzeme',
    options: malzemeOptions,
    style: { width: 200 },
    showSearch: true,
    optionFilterProp: 'label',
    onSearch: (val) => setMalzemeArama(val),
    notFoundContent: malzemeArama ? malzemeNotFound : null,
  };

  const handleKaydet = async (values) => {
    if (!urun?.id) return;
    try {
      const porsiyon = values.recetePorsiyon || 1;
      const kalemler = (values.kalemler || []).map(k => donusturKalem(k, porsiyon));
      if (editId) {
        // Mevcut reçeteyi güncelle (aktiflik durumu değişmez)
        await urunRecetelerAPI.update(editId, {
          aciklama: (urun.urun_adi || urun.ad || '') + ' reçetesi',
          porsiyon,
          kalemler,
        });
        message.success('Reçete güncellendi');
      } else {
        // Yeni reçete oluştur ve aktif yap
        await urunRecetelerAPI.create({
          urun_id: urun.id,
          aciklama: (urun.urun_adi || urun.ad || '') + ' reçetesi',
          porsiyon,
          kalemler,
          aktif: true,
        });
        message.success('Yeni reçete eklendi ve aktif yapıldı');
      }
      setEditId(null);
      form.resetFields();
      await fetchReceteler();
      onSaved?.();
    } catch (err) {
      message.error('Kaydedilemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const gercekMaliyet = (r) => {
    if (r.malzeme_birim_fiyat == null) return null;
    const fiyat = Number(r.malzeme_birim_fiyat);
    const miktar = Number(r.miktar);
    if (r.birim === r.malzeme_birim) return miktar * fiyat;
    if ((r.birim === 'gr' && r.malzeme_birim === 'kg') || (r.birim === 'ml' && r.malzeme_birim === 'lt')) return (miktar / 1000) * fiyat;
    if ((r.birim === 'kg' && r.malzeme_birim === 'gr') || (r.birim === 'lt' && r.malzeme_birim === 'ml')) return (miktar * 1000) * fiyat;
    return miktar * fiyat;
  };

  const aktifRecete = receteler.find(r => r.aktif === 1);

  return (
    <>
      <Modal
        title={urun ? `${urun.urun_adi || urun.ad} - Reçete` : 'Reçete'}
        open={open}
        onCancel={() => { form.resetFields(); setEditId(null); onClose?.(); }}
        footer={null}
        width={780}
      >
        {/* Reçete listesi */}
        <Card size="small" title="Reçete Sürümleri" style={{ marginBottom: 12 }}
          extra={
            <Button size="small" type={editId === null ? 'primary' : 'default'} icon={<PlusOutlined />} onClick={handleYeniAc}>
              Yeni Reçete
            </Button>
          }
        >
          {receteler.length === 0 ? (
            <Empty description="Henüz reçete yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              size="small"
              pagination={false}
              dataSource={receteler}
              rowKey="id"
              columns={[
                {
                  title: 'Durum', dataIndex: 'aktif', width: 90,
                  render: (v) => v === 1
                    ? <Tag color="green" icon={<CheckCircleFilled />}>Aktif</Tag>
                    : <Tag>Pasif</Tag>,
                },
                {
                  title: 'Tarih', dataIndex: 'olusturma_tarihi', width: 130,
                  render: (v) => v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '-',
                },
                {
                  title: 'Malzeme', dataIndex: 'kalemler',
                  render: (k) => `${(k || []).length} kalem`,
                },
                {
                  title: 'Porsiyon', dataIndex: 'porsiyon', width: 80, align: 'right',
                  render: (v) => v || 1,
                },
                {
                  title: 'İşlem', key: 'islem', width: 220, align: 'right',
                  render: (_, r) => (
                    <Space size={4}>
                      {r.aktif !== 1 && (
                        <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleAktiveEt(r.id)}>
                          Aktif Yap
                        </Button>
                      )}
                      <Button size="small" icon={<EditOutlined />} onClick={() => handleEditAc(r)}>
                        Düzenle
                      </Button>
                      {r.aktif !== 1 && (
                        <Popconfirm title="Bu reçete sürümünü silmek istediğinize emin misiniz?" onConfirm={() => handleSil(r.id)} okText="Sil" cancelText="İptal">
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                },
              ]}
              expandable={{
                expandedRowRender: (r) => (
                  (r.kalemler && r.kalemler.length > 0) ? (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={r.kalemler}
                      rowKey="malzeme_id"
                      columns={[
                        { title: 'Malzeme', dataIndex: 'malzeme_adi' },
                        { title: 'Miktar', key: 'miktar', render: (_, k) => k.gosterim_miktar ? `${k.gosterim_miktar} ${k.gosterim_birim}` : `${k.miktar} ${k.birim}` },
                        { title: 'Gerçek', key: 'gercek', render: (_, k) => `${k.miktar} ${k.birim}` },
                        {
                          title: 'Maliyet', key: 'maliyet', align: 'right',
                          render: (_, k) => {
                            const m = gercekMaliyet(k);
                            return m == null ? <span style={{ color: '#999' }}>-</span> : `${formatSayi(m, 2)} ₺`;
                          },
                        },
                      ]}
                    />
                  ) : <span style={{ color: '#999' }}>Bu sürümde malzeme yok</span>
                ),
              }}
            />
          )}
        </Card>

        <Divider>{editId ? 'Reçeteyi Düzenle' : (receteler.length === 0 ? 'Yeni Reçete Oluştur' : 'Yeni Sürüm Ekle')}</Divider>

        <Form form={form} layout="vertical" onFinish={handleKaydet}>
          <Form.Item
            name="recetePorsiyon"
            label={editId ? 'Bu miktarlar kaç porsiyon için girilmişti?' : 'Kaç porsiyon için malzeme giriyorsunuz?'}
            initialValue={1}
            rules={[{ required: true, message: 'Porsiyon sayısı zorunludur' }]}
            extra={editId
              ? 'Girişte kullanılan porsiyon sayısı hatırlandı. Değiştirirseniz miktarlar yeni sayıya göre yeniden hesaplanır.'
              : 'Girdiğiniz miktarlar bu sayıya bölünerek 1 porsiyonluk reçete oluşturulur.'}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="Örn: 50" />
          </Form.Item>

          <Form.List name="kalemler">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...restField} name={[name, 'malzeme_id']} rules={[{ required: true, message: 'Malzeme seçin' }]}>
                      <Select {...malzemeSelectProps} onChange={(val) => handleMalzemeChange(val, ['kalemler', name, 'birim_id'])} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'miktar']} rules={[{ required: true, message: 'Miktar girin' }]}>
                      <InputNumber placeholder="Miktar" min={0.001} step={0.1} precision={3} />
                    </Form.Item>
                    <Form.Item noStyle dependencies={[['kalemler', name, 'malzeme_id']]}>
                      {() => {
                        const mid = form.getFieldValue(['kalemler', name, 'malzeme_id']);
                        const opts = getSecilebilenBirimler(mid);
                        const m = malzemeler.find(mal => mal.id === mid);
                        return (
                          <Space.Compact>
                            <Form.Item {...restField} name={[name, 'birim_id']} rules={[{ required: true, message: 'Birim seçin' }]} noStyle>
                              <Select style={{ width: 90 }} options={opts} disabled={opts.length <= 1} />
                            </Form.Item>
                            {m && (
                              <Button icon={<SwapOutlined />} title="Birim dönüşümü ekle"
                                onClick={() => { setDonusumMalzeme(m); setDonusumOpen(true); }} />
                            )}
                          </Space.Compact>
                        );
                      }}
                    </Form.Item>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                  Malzeme Ekle
                </Button>
              </>
            )}
          </Form.List>

          {!editId && aktifRecete && (
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
              Bu yeni sürüm <strong>aktif olarak</strong> kaydedilecek; mevcut aktif sürüm pasife alınacak (geçmiş üretimler etkilenmez).
            </div>
          )}

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            {editId ? (
              <Button onClick={handleYeniAc}>Vazgeç</Button>
            ) : <span />}
            <Button type="primary" htmlType="submit">
              {editId ? 'Reçeteyi Güncelle' : 'Yeni Sürümü Kaydet'}
            </Button>
          </Space>
        </Form>
      </Modal>

      <BirimDonusumModal
        open={donusumOpen}
        malzeme={donusumMalzeme}
        birimler={birimler}
        birimlerRaw={birimlerRaw}
        onClose={() => { setDonusumOpen(false); setDonusumMalzeme(null); }}
        onSaved={() => fetchBirimDonusumleri()}
      />

      <Modal
        title="Hızlı Malzeme Ekle"
        open={malzemeModalOpen}
        onCancel={() => { setMalzemeModalOpen(false); malzemeForm.resetFields(); }}
        onOk={() => malzemeForm.submit()}
        zIndex={1100}
      >
        <Form form={malzemeForm} layout="vertical" onFinish={handleMalzemeKaydet}>
          <Form.Item name="ad" label="Ad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="marka" label="Marka">
            <Input />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="malzeme_tipi_id" label="Tip">
            <Select options={malzemeTipleri} />
          </Form.Item>
          <Form.Item name="birim_id" label="Birim">
            <Select options={birimler} />
          </Form.Item>
          <Form.Item name="kritik_seviye" label="Kritik Seviye">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default ReceteModal;
